import { AutoCompleteInput, CustomSelect, CustomInput, PhoneInput } from '@/shared/ui/components';
import { phoneFormat } from '@/shared/lib';
import { Switch } from 'antd';
import { t } from 'ttag';
import { GOOGLE_API_KEY } from '@/shared/config';
import { useAutoCompleteAddress } from './useAutoCompleteAddress';
import AddressDataSkeleton from './addressDataSkeleton';
import type { AutoCompleteAddressProps } from './autoCompleteAddress.types';
import './styles.scss';

const AutoCompleteAddress = ({
  countries,
  facilityId,
  onChangeDataAddress,
  onChangeDivision,
  errorObject,
  isSameAsShippingAddress,
  updateIsSameShippingAddress,
  initialShippingAddress,
  initialReturningAddress,
}: AutoCompleteAddressProps) => {
  const {
    state,
    setState,
    handleKeyDown,
    onChangeSelectionAutoComplete,
    onChangeInputAutoComplete,
    onChangeAutoComplete,
    clearAutoComplete,
    onChangeDataAutoComplete,
  } = useAutoCompleteAddress({
    countries,
    facilityId,
    onChangeDataAddress,
    onChangeDivision,
    isSameAsShippingAddress,
    initialShippingAddress,
    initialReturningAddress,
  });

  /**
   * Renders the shipping-address fields of the RMA address form, headed by a note that the address is auto-filled from the clinic contact info: country select, autocompleted address and city, state as a select for US and CA and a free-text input otherwise, zip code, country-aware phone number and contact email. Binds each field to the 'shipping' autocomplete change, selection, input and clear handlers and surfaces the matching errorObject message.
   *
   * @returns The shipping-address form fields.
   */
  const shippingAddress = () => (
    <>
      <div className="sub-row-title">{t`The shipping address is auto-filled from your clinic contact info.`}</div>
      <CustomSelect
        id="shippingCountry"
        name="country"
        title={t`Country`}
        aria-label={t`Country`}
        value={state.shippingCountryObject}
        placeholder={t`Enter country`}
        options={countries}
        isError={!!errorObject.shippingCountry}
        errorMessage={errorObject.shippingCountry}
        size="middle"
        onChange={(name, value, options) => {
          onChangeSelectionAutoComplete(name, options, 'shipping');
        }}
        onInputKeyDown={handleKeyDown}
      />

      <AutoCompleteInput
        googleApiKey={GOOGLE_API_KEY}
        className="w-100 row-address"
        isSearchCity={false}
        id="shippingAddress"
        onInputKeyDown={handleKeyDown}
        title={t`Address`}
        placeholder={t`Enter address`}
        country={state.shippingAddressData.country}
        value={state.shippingAddressData.address}
        onChangeInput={(value, isSearchCity) =>
          onChangeInputAutoComplete(value, isSearchCity, 'shipping')
        }
        onChangeAddress={(data, isSearchCity) =>
          onChangeAutoComplete(data, isSearchCity, 'shipping')
        }
        eventClear={(isSearchCity) => clearAutoComplete(isSearchCity, 'shipping')}
        isError={!!errorObject.shippingAddress}
        errorMessage={errorObject.shippingAddress}
        allowClear={false}
        size="small"
      />

      <div className="row-city-state">
        <AutoCompleteInput
          googleApiKey={GOOGLE_API_KEY}
          className="w-100 row-address"
          isSearchCity
          id="shippingCity"
          onInputKeyDown={handleKeyDown}
          title={t`City`}
          placeholder={t`Enter city`}
          country={state.shippingAddressData.country}
          value={state.shippingAddressData.city}
          onChangeInput={(value, isSearchCity) =>
            onChangeInputAutoComplete(value, isSearchCity, 'shipping')
          }
          onChangeAddress={(data, isSearchCity) =>
            onChangeAutoComplete(data, isSearchCity, 'shipping')
          }
          eventClear={(isSearchCity) => clearAutoComplete(isSearchCity, 'shipping')}
          isError={!!errorObject.shippingCity}
          errorMessage={errorObject.shippingCity}
          allowClear={false}
          size="small"
        />
        {state.shippingAddressData.country === 'US' ||
        state.shippingCountryObject?.value === 'US' ||
        state.shippingAddressData.country === 'CA' ||
        state.shippingCountryObject?.value === 'CA' ? (
          <CustomSelect
            onInputKeyDown={handleKeyDown}
            className="w-100 row-address"
            name="state"
            id="shippingState"
            title={t`State`}
            aria-label={t`State`}
            options={state.shippingDivisions}
            placeholder={t`Select a state`}
            value={state.shippingStateObject}
            onChange={(name, value, options) => {
              onChangeSelectionAutoComplete(name, options, 'shipping');
            }}
            isError={!!errorObject.shippingState}
            errorMessage={errorObject.shippingState}
            size="middle"
          />
        ) : (
          <CustomInput
            onKeyDown={handleKeyDown}
            className="w-100 row-address"
            name="state"
            id="shippingState"
            title={t`State`}
            placeholder={t`Enter state`}
            value={state.shippingAddressData.state}
            onChange={(name, value) => onChangeDataAutoComplete(name, value, 'shipping')}
            isError={!!errorObject.shippingState}
            errorMessage={errorObject.shippingState}
            size="middle"
          />
        )}
      </div>

      <CustomInput
        className="w-100 row-address"
        name="zip"
        id="shippingZip"
        onKeyDown={handleKeyDown}
        title={t`Zip code`}
        placeholder={t`Enter zip code`}
        value={state.shippingAddressData.zip}
        onChange={(name, value) => onChangeDataAutoComplete(name, value, 'shipping', 'shippingZip')}
        isError={!!errorObject.shippingZip}
        errorMessage={errorObject.shippingZip}
        size="middle"
      />

      <PhoneInput
        className="w-100 row-address"
        title={t`Contact phone number`}
        name="phoneNumber"
        id="shippingContactPhone"
        onKeyDown={handleKeyDown}
        countryValue={state.shippingAddressData.country}
        value={phoneFormat(
          state.shippingAddressData.phoneNumber,
          state.shippingAddressData.country,
        )}
        placeholder={t`Enter phone number`}
        onChange={(name, value) =>
          onChangeDataAutoComplete(name, value, 'shipping', 'shippingPhone')
        }
        size="small"
        isError={!!errorObject.shippingPhone}
        errorMessage={errorObject.shippingPhone}
        maxLength={15}
        countryOptions={countries}
      />

      <CustomInput
        className="row-address"
        name="email"
        id="shippingContactEmail"
        title={t`Contact Email`}
        placeholder={t`Enter contact email`}
        value={state.shippingAddressData.email}
        onChange={(name, value) =>
          onChangeDataAutoComplete(name, value, 'shipping', 'shippingEmail')
        }
        isError={!!errorObject.shippingEmail}
        errorMessage={errorObject.shippingEmail}
        size="middle"
      />
    </>
  );

  /**
   * Renders the returning-address fields of the RMA address form: country select, autocompleted address and city, state as a select for US and CA and a free-text input otherwise, zip code, country-aware phone number and contact email. Disables every field while 'same as shipping address' is checked, suppresses the errorObject messages in that case, and routes each edit through the 'returning' variants of the autocomplete change, selection, input and clear handlers.
   *
   * @returns The returning-address form fields.
   */
  const returningAddress = () => (
    <>
      <CustomSelect
        className="w-100 custom-input-margin-top"
        name="country"
        id="returningCountry"
        onInputKeyDown={handleKeyDown}
        title={t`Country`}
        aria-label={t`Country`}
        options={countries}
        value={state.returningCountryObject}
        onChange={(name, value, options) => {
          onChangeSelectionAutoComplete(name, options, 'returning');
        }}
        placeholder={t`Enter country`}
        disabled={state.isSameAsShippingAddress}
        size="middle"
      />

      <AutoCompleteInput
        googleApiKey={GOOGLE_API_KEY}
        className="w-100 row-address"
        isSearchCity={false}
        id="returningAddress"
        onInputKeyDown={handleKeyDown}
        title={t`Address`}
        placeholder={t`Enter address`}
        country={state.returningAddressData.country}
        value={state.returningAddressData.address}
        onChangeInput={(value, isSearchCity) =>
          onChangeInputAutoComplete(value, isSearchCity, 'returning')
        }
        onChangeAddress={(data, isSearchCity) =>
          onChangeAutoComplete(data, isSearchCity, 'returning')
        }
        eventClear={(isSearchCity) => clearAutoComplete(isSearchCity, 'returning')}
        disabled={state.isSameAsShippingAddress}
        isError={!state.isSameAsShippingAddress && !!errorObject.returningAddress}
        errorMessage={!state.isSameAsShippingAddress && errorObject.returningAddress}
        allowClear={false}
        size="small"
      />

      <div className="row-city-state">
        <AutoCompleteInput
          googleApiKey={GOOGLE_API_KEY}
          className="w-100 row-address"
          isSearchCity
          id="returningCity"
          onInputKeyDown={handleKeyDown}
          title={t`City`}
          placeholder={t`Enter city`}
          country={state.returningAddressData.country}
          value={state.returningAddressData.city}
          onChangeInput={(value, isSearchCity) =>
            onChangeInputAutoComplete(value, isSearchCity, 'returning')
          }
          onChangeAddress={(data, isSearchCity) =>
            onChangeAutoComplete(data, isSearchCity, 'returning')
          }
          eventClear={(isSearchCity) => clearAutoComplete(isSearchCity, 'returning')}
          disabled={state.isSameAsShippingAddress}
          isError={!state.isSameAsShippingAddress && !!errorObject.returningCity}
          errorMessage={!state.isSameAsShippingAddress && errorObject.returningCity}
          allowClear={false}
          size="small"
        />
        {state.returningAddressData.country === 'US' ||
        state.returningAddressData.country === 'CA' ||
        state.returningAddressData.country === 'CA' ||
        state.returningCountryObject?.value === 'CA' ||
        (state.shippingAddressData.country === 'US' && state.isSameAsShippingAddress) ||
        (state.shippingCountryObject?.value === 'CA' && state.isSameAsShippingAddress) ||
        state.returningCountryObject?.value === 'US' ||
        state.returningCountryObject?.value === 'CA' ? (
          <CustomSelect
            onInputKeyDown={handleKeyDown}
            className="w-100 row-address"
            name="state"
            id="returningState"
            title={t`State`}
            aria-label={t`State`}
            options={state.returningDivisions}
            placeholder={t`Select a state`}
            value={state.returningStateObject}
            onChange={(name, value, options) => {
              onChangeSelectionAutoComplete(name, options, 'returning');
            }}
            disabled={state.isSameAsShippingAddress}
            isError={!state.isSameAsShippingAddress && !!errorObject.returningState}
            errorMessage={!state.isSameAsShippingAddress && errorObject.returningState}
            size="middle"
          />
        ) : (
          <CustomInput
            onKeyDown={handleKeyDown}
            className="w-100 row-address"
            name="state"
            id="returningState"
            title={t`State`}
            placeholder={t`Enter state`}
            value={state.returningAddressData.state}
            onChange={(name, value) => onChangeDataAutoComplete(name, value, 'returning')}
            disabled={state.isSameAsShippingAddress}
            isError={!state.isSameAsShippingAddress && !!errorObject.returningState}
            errorMessage={!state.isSameAsShippingAddress && errorObject.returningState}
            size="middle"
          />
        )}
      </div>

      <CustomInput
        className="w-100 row-address"
        name="zip"
        id="returningZip"
        onKeyDown={handleKeyDown}
        title={t`Zip code`}
        placeholder={t`Enter zip code`}
        value={state.returningAddressData.zip}
        onChange={(name, value) =>
          onChangeDataAutoComplete(name, value, 'returning', 'returningZip')
        }
        disabled={state.isSameAsShippingAddress}
        isError={!state.isSameAsShippingAddress && !!errorObject.returningZip}
        errorMessage={!state.isSameAsShippingAddress && errorObject.returningZip}
        size="middle"
      />

      <PhoneInput
        className="w-100 row-address"
        title={t`Contact phone number`}
        name="phoneNumber"
        id="returningContactPhone"
        onKeyDown={handleKeyDown}
        countryValue={state.returningAddressData.country}
        value={phoneFormat(
          state.returningAddressData.phoneNumber,
          state.returningAddressData.country,
        )}
        placeholder={t`Enter phone number`}
        onChange={(name, value) =>
          onChangeDataAutoComplete(name, value, 'returning', 'returningPhone')
        }
        size="small"
        maxLength={15}
        disabled={state.isSameAsShippingAddress}
        isError={!state.isSameAsShippingAddress && !!errorObject.returningPhone}
        errorMessage={!state.isSameAsShippingAddress && errorObject.returningPhone}
        countryOptions={countries}
      />

      <CustomInput
        className="row-address"
        name="email"
        id="returningContactEmail"
        title={t`Contact Email`}
        placeholder={t`Enter contact email`}
        value={state.returningAddressData.email}
        onChange={(name, value) =>
          onChangeDataAutoComplete(name, value, 'returning', 'returningEmail')
        }
        disabled={state.isSameAsShippingAddress}
        isError={!state.isSameAsShippingAddress && !!errorObject.returningEmail}
        errorMessage={!state.isSameAsShippingAddress && errorObject.returningEmail}
        size="middle"
      />
    </>
  );

  return (
    <div className="rma-address-shipping-info flex">
      <div className="left-column">
        <div className="row-title">{t`Shipping address`}</div>
        {state.isLoadedData ? shippingAddress() : <AddressDataSkeleton />}
      </div>

      <div className="vertical-line" />

      <div className="right-column">
        <div className="d-flex">
          <div className="row-title">{t`Returning address`}</div>
          <div className="switch-address">
            <span className="switch-label">{t`Same as shipping address`}</span>
            <Switch
              loading={!state.isLoadedData}
              checked={state.isSameAsShippingAddress}
              aria-label={t`Same as shipping address`}
              onChange={() => {
                const updatedIsSameAsShippingAddress = !state.isSameAsShippingAddress;
                updateIsSameShippingAddress(updatedIsSameAsShippingAddress);
                setState({ isSameAsShippingAddress: updatedIsSameAsShippingAddress });
              }}
            />
          </div>
        </div>
        {state.isLoadedData ? returningAddress() : <AddressDataSkeleton />}
      </div>
    </div>
  );
};

export default AutoCompleteAddress;
